import { useCallback, useMemo } from 'react'
import structureTypes from 'common/constants/structureTypes'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { useActions } from 'common/hooks/useActions'
import * as actions from 'client/actions/actionsManagement'
import { useSetEntityHighlighted } from 'client/context/HighlightedContext'
import { useManagement, usePage } from 'client/store'
import managementSelectors from 'client/store/management/managementSelectors'
import pageSelectors from 'client/store/page/pageSelectors'
import { Entities } from 'common/utils/entityUtils'
import { useBreadcrumbsMeasure } from './use-breadcrumbs-measure'

const omitEntityTypes = (entity: Entities[number]) =>
  [
    structureTypes.TWO_STEP_PAYMENT_FORM_STEP_PAYMENT,
    structureTypes.TWO_STEP_PAYMENT_FORM_STEP_OPT_IN,
    structureTypes.COLUMN,
    EntityTypeEnum.Column,
    EntityTypeEnum.FaqItem,
    EntityTypeEnum.CarouselItem,
    structureTypes.CAROUSEL_ITEM,
  ].includes(entity.type) === false

export const useBreadcrumbs = () => {
  const editingId = useManagement(managementSelectors.getEditingId)
  const breadcrumbs = usePage(page =>
    pageSelectors.getBreadcrumbs(page, editingId || ''),
  )
  const setHighlightedId = useSetEntityHighlighted()
  const toggleSettingsAction = useActions(actions.toggleSettings)

  const breadcrumbsValues = useMemo(
    () => Object.values(breadcrumbs),
    [breadcrumbs],
  )
  const filteredBreadcrumbs = useMemo(
    () => breadcrumbsValues.filter(omitEntityTypes),
    [breadcrumbsValues],
  )

  const { setRef, setMeasureRef, allowedItemsCount } = useBreadcrumbsMeasure({
    itemsCount: filteredBreadcrumbs.length,
  })

  const visibleBreadcrumbs = useMemo(
    () =>
      filteredBreadcrumbs
        .slice(0, allowedItemsCount ?? filteredBreadcrumbs.length)
        .reverse(),
    [filteredBreadcrumbs, allowedItemsCount],
  )

  const handleHighlight = useCallback(
    (id?: string) => () => setHighlightedId(id ?? ''),
    [setHighlightedId],
  )
  const handleToggleSettings = useCallback(
    (id: string) => () => toggleSettingsAction(id),
    [toggleSettingsAction],
  )

  return {
    setRef,
    setMeasureRef,
    filteredBreadcrumbs,
    visibleBreadcrumbs,
    handleHighlight,
    handleToggleSettings,
  }
}
